import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Check, Mail, Phone, Instagram, MapPin } from "lucide-react";
import { BRAND } from "../data/site";
import { SparkleIcon, LipstickIcon, CombIcon } from "../components/BrandIcons";
import { useContent } from "../context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", email: "", phone: "",
  event_type: "", event_date: "", location: "",
  people_count: "", service_type: "", appointment_type: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const { content } = useContent();
  const location = useLocation();
  const brand = { ...BRAND, ...(content.brand || {}), instagramHandle: content.brand?.instagram_handle || BRAND.instagramHandle };
  const cp = content.contact_page || {};

  // Scroll to the form when arriving via /contact#enquiry-form
  useEffect(() => {
    if (location.hash === "#enquiry-form") {
      const el = document.getElementById("enquiry-form");
      if (el) {
        // Small delay so layout is stable
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      }
    }
  }, [location.hash, location.key]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setStatus({ state: "error", msg: "Please add your name and email so I can reach you." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus({ state: "success", msg: cp.success_message || "Got it — enquiry received. I’ll be in touch within 1–2 business days." });
      setForm(empty);
    } catch (err) {
      setStatus({ state: "error", msg: cp.error_message || "Something glitched. Try again, or email me directly." });
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="grain-coral border-b-2 border-ink relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-16 md:py-24 md:px-10 grid gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="eyebrow text-ink text-xs">{cp.eyebrow || "Contact"}</p>
            <h1 className="heading mt-3 text-4xl md:text-6xl text-ink">{cp.headline || "Tell me about the vibe."}</h1>
            <p className="mt-5 max-w-xl text-lg text-ink/90">{cp.intro || "Drop the details below and I’ll respond within 1–2 business days. The more you tell me, the better I can plan the day."}</p>
          </div>
          <div className="md:col-span-5">
            <div className="grain-pink card-brut p-6 relative overflow-hidden space-y-4">
              <div className="flex items-center gap-3"><Phone className="h-5 w-5 text-ink" /> <a href={`tel:${(brand.phone||'').replace(/\s/g,'')}`} className="text-ink hover:underline" data-testid="contact-phone-link">{brand.phone}</a></div>
              <div className="flex items-center gap-3"><Mail className="h-5 w-5 text-ink" /> <a href={`mailto:${brand.email}`} className="text-ink hover:underline break-all" data-testid="contact-email-link">{brand.email}</a></div>
              <div className="flex items-center gap-3"><Instagram className="h-5 w-5 text-ink" /> <a href={brand.instagram} target="_blank" rel="noreferrer" className="text-ink hover:underline" data-testid="contact-instagram-link">{brand.instagramHandle}</a></div>
              <div className="flex items-center gap-3"><MapPin className="h-5 w-5 text-ink" /> <span className="text-ink">{brand.location} — mobile & studio</span></div>
              <div className="absolute right-3 top-3 text-ink/80"><LipstickIcon className="h-7 w-7" /></div>
              <div className="absolute right-3 bottom-3 text-ink/80"><CombIcon className="h-7 w-7" /></div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream">
        <div className="mx-auto max-w-4xl px-5 py-16 md:py-20 md:px-10">
          <form id="enquiry-form" onSubmit={submit} className="card-brut bg-cream p-6 md:p-10 space-y-5 scroll-mt-24" data-testid="enquiry-form">
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <label className="label" htmlFor="name">Your name *</label>
                <input id="name" required className="field" value={form.name} onChange={set("name")} placeholder="First & last" data-testid="form-name" />
              </div>
              <div>
                <label className="label" htmlFor="email">Email *</label>
                <input id="email" type="email" required className="field" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="form-email" />
              </div>
              <div>
                <label className="label" htmlFor="phone">Phone</label>
                <input id="phone" className="field" value={form.phone} onChange={set("phone")} placeholder="0400 000 000" data-testid="form-phone" />
              </div>
              <div>
                <label className="label" htmlFor="event_type">Event type</label>
                <select id="event_type" className="field" value={form.event_type} onChange={set("event_type")} data-testid="form-event-type">
                  <option value="">Select one…</option>
                  <option>Wedding</option>
                  <option>Formal / Race day</option>
                  <option>Photoshoot / Editorial</option>
                  <option>Film / TV / Theatre</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="label" htmlFor="event_date">Event date</label>
                <input id="event_date" type="date" className="field" value={form.event_date} onChange={set("event_date")} data-testid="form-event-date" />
              </div>
              <div>
                <label className="label" htmlFor="location">Location</label>
                <input id="location" className="field" value={form.location} onChange={set("location")} placeholder="Suburb / venue" data-testid="form-location" />
              </div>
              <div>
                <label className="label" htmlFor="people_count">Number of people</label>
                <input id="people_count" className="field" value={form.people_count} onChange={set("people_count")} placeholder="e.g. 1, 4, 8…" data-testid="form-people" />
              </div>
              <div>
                <label className="label" htmlFor="service_type">Hair, makeup or both?</label>
                <select id="service_type" className="field" value={form.service_type} onChange={set("service_type")} data-testid="form-service-type">
                  <option value="">Select one…</option>
                  <option>Makeup</option>
                  <option>Hair</option>
                  <option>Both</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label" htmlFor="appointment_type">Mobile service or studio appointment?</label>
                <select id="appointment_type" className="field" value={form.appointment_type} onChange={set("appointment_type")} data-testid="form-appointment-type">
                  <option value="">Select one…</option>
                  <option>Mobile — I come to you</option>
                  <option>Studio appointment — Ardeer</option>
                  <option>Not sure yet</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label" htmlFor="message">Tell me about the vibe</label>
                <textarea id="message" className="field min-h-[140px]" value={form.message} onChange={set("message")} placeholder="References, mood, song, the colour palette of your favourite movie — go." data-testid="form-message" />
              </div>
            </div>

            {status.state === "success" && (
              <div className="border-2 border-ink bg-pinkpanel p-4 flex items-center gap-3" data-testid="form-success">
                <Check className="h-5 w-5 text-ink" />
                <p className="text-ink">{status.msg}</p>
              </div>
            )}
            {status.state === "error" && (
              <div className="border-2 border-ink bg-coral/80 p-4" data-testid="form-error"><p className="text-ink">{status.msg}</p></div>
            )}

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button type="submit" disabled={status.state === "loading"} className="btn-brut" data-testid="form-submit">
                {status.state === "loading" ? "Sending…" : (cp.submit_label || "Send enquiry")} <SparkleIcon className="h-4 w-4" />
              </button>
              <p className="text-sm text-ink/70">{cp.footer_note || "No spam, no sales tactics. Just a real reply."}</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
